import React from 'react';
import {
  Users,
  Receipt,
  AlertTriangle,
  TrendingUp,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { useAdminStats } from '@/hooks/useDashboard';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  change?: {
    value: number;
    label: string;
  };
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  change,
  variant = 'default',
}) => {
  const variantClasses = {
    default: 'bg-gray-100 text-gray-600 dark:bg-secondary-700 dark:text-gray-400',
    success: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    warning: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    danger: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {value}
            </p>
            {change && (
              <div className="flex items-center mt-2 text-sm">
                {change.value >= 0 ? (
                  <ArrowUpRight className="w-4 h-4 text-green-500 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span
                  className={
                    change.value >= 0 ? 'text-green-600' : 'text-red-600'
                  }
                >
                  {Math.abs(change.value)}%
                </span>
                <span className="text-gray-500 dark:text-gray-400 ml-1">
                  {change.label}
                </span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${variantClasses[variant]}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const AdminDashboard: React.FC = () => {
  const { data: stats, isLoading, error } = useAdminStats();

  if (isLoading) {
    return <PageLoader message="Carregando estatisticas..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Erro ao carregar dados
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Nao foi possivel carregar as estatisticas. Tente novamente.
        </p>
      </div>
    );
  }

  // Default values for missing data
  const defaultStats = {
    users: { total: 0, active: 0, new24h: 0, new7d: 0 },
    transactions: {
      total: 0,
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
      totalAmount: 0,
    },
    receipts: {
      total: 0,
      processed: 0,
      pending: 0,
      failed: 0,
      successRate: 0,
    },
    errors: {
      total: 0,
      byType: {},
      bySeverity: { low: 0, medium: 0, high: 0, critical: 0 },
      byStatus: { new: 0, investigating: 0, resolved: 0, ignored: 0 },
      last24Hours: 0,
      last7Days: 0,
      resolutionRate: 0,
      averageResolutionTime: 0,
    },
    systemHealth: {
      apiLatency: 0,
      ocrLatency: 0,
      databaseConnections: 0,
      queueSize: 0,
    },
  };

  // Merge API data with defaults to prevent undefined errors
  const mockStats = {
    users: { ...defaultStats.users, ...stats?.users },
    transactions: { ...defaultStats.transactions, ...stats?.transactions },
    receipts: { ...defaultStats.receipts, ...stats?.receipts },
    errors: { ...defaultStats.errors, ...stats?.errors },
    systemHealth: { ...defaultStats.systemHealth, ...stats?.systemHealth },
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Dashboard Administrativo
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Visao geral do sistema KeepNota
        </p>
      </div>

      {/* Main stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Usuarios Totais"
          value={mockStats.users.total.toLocaleString()}
          icon={<Users className="w-6 h-6" />}
          change={{ value: 12, label: 'vs mes anterior' }}
          variant="default"
        />
        <StatCard
          title="Recibos Processados"
          value={mockStats.receipts.processed.toLocaleString()}
          icon={<Receipt className="w-6 h-6" />}
          change={{ value: 8, label: 'vs semana anterior' }}
          variant="success"
        />
        <StatCard
          title="Erros (24h)"
          value={mockStats.errors.last24Hours}
          icon={<AlertTriangle className="w-6 h-6" />}
          change={{ value: -15, label: 'vs ontem' }}
          variant="danger"
        />
        <StatCard
          title="Taxa de Sucesso"
          value={`${mockStats.receipts.successRate}%`}
          icon={<TrendingUp className="w-6 h-6" />}
          variant="success"
        />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Users card */}
        <Card>
          <CardHeader>
            <CardTitle>Usuarios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Usuarios Ativos
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {mockStats.users.active.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Novos (24h)
                </p>
                <p className="text-xl font-bold text-green-600 dark:text-green-400">
                  +{mockStats.users.new24h}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Novos (7d)
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  +{mockStats.users.new7d}
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Taxa Ativacao
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {((mockStats.users.active / mockStats.users.total) * 100).toFixed(
                    1
                  )}
                  %
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Receipts card */}
        <Card>
          <CardHeader>
            <CardTitle>Recibos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500 dark:text-gray-400">
                    Processados
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {mockStats.receipts.processed.toLocaleString()} /{' '}
                    {mockStats.receipts.total.toLocaleString()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-secondary-700 rounded-full h-2.5">
                  <div
                    className="bg-green-500 h-2.5 rounded-full"
                    style={{
                      width: `${
                        (mockStats.receipts.processed / mockStats.receipts.total) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-3 gap-4 pt-2">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-yellow-600 dark:text-yellow-400">
                    <Clock className="w-4 h-4" />
                    <span className="font-bold">
                      {mockStats.receipts.pending}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Pendentes
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-green-600 dark:text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-bold">
                      {mockStats.receipts.successRate}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Sucesso
                  </p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-red-600 dark:text-red-400">
                    <XCircle className="w-4 h-4" />
                    <span className="font-bold">{mockStats.receipts.failed}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Falhas
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System health */}
      <Card>
        <CardHeader>
          <CardTitle>Saude do Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  API Latency
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {mockStats.systemHealth.apiLatency}ms
              </p>
              <Badge
                variant={mockStats.systemHealth.apiLatency < 100 ? 'success' : 'warning'}
                className="mt-2"
              >
                {mockStats.systemHealth.apiLatency < 100 ? 'Normal' : 'Alto'}
              </Badge>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Receipt className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  OCR Latency
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {(mockStats.systemHealth.ocrLatency / 1000).toFixed(1)}s
              </p>
              <Badge
                variant={mockStats.systemHealth.ocrLatency < 3000 ? 'success' : 'warning'}
                className="mt-2"
              >
                {mockStats.systemHealth.ocrLatency < 3000 ? 'Normal' : 'Lento'}
              </Badge>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  DB Connections
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {mockStats.systemHealth.databaseConnections}
              </p>
              <Badge variant="success" className="mt-2">
                Saudavel
              </Badge>
            </div>

            <div className="p-4 bg-gray-50 dark:bg-secondary-700/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Fila de Processamento
                </span>
              </div>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {mockStats.systemHealth.queueSize}
              </p>
              <Badge
                variant={mockStats.systemHealth.queueSize < 100 ? 'success' : 'warning'}
                className="mt-2"
              >
                {mockStats.systemHealth.queueSize < 100 ? 'Normal' : 'Congestionado'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error summary */}
      <Card>
        <CardHeader>
          <CardTitle>Resumo de Erros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {mockStats.errors.bySeverity.critical}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Criticos</p>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {mockStats.errors.bySeverity.high}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Altos</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {mockStats.errors.bySeverity.medium}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Medios</p>
            </div>
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {mockStats.errors.bySeverity.low}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Baixos</p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-secondary-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Taxa de Resolucao
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {mockStats.errors.resolutionRate}%
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tempo Medio Resolucao
                </p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {mockStats.errors.averageResolutionTime}h
                </p>
              </div>
              <a
                href="/admin/errors"
                className="text-primary-600 dark:text-primary-400 hover:underline text-sm font-medium"
              >
                Ver todos os erros
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
